import React, { useState } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Select,
  message,
  Popconfirm,
  DatePicker,
  Space,
  Input,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { adminApi, Contract, CreateContractData } from "@app/api/admin";
import { useUpdateEffect } from "@app/hooks/useUpdateEffect";
import { useCompanyList } from "../../CompanyPage";
import { ColumnsType } from "antd/lib/table";
import moment from "moment";

interface ContractManagementProps {
  employeeId?: number;
  employeeName?: string;
}



const ContractManagement: React.FC<ContractManagementProps> = ({
  employeeId,
  employeeName
}) => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingContract, setEditingContract] = useState<Contract | null>(null);
  const [form] = Form.useForm();

  // 获取合同列表
  const fetchContracts = async () => {
    try {
      setLoading(true);
      const { data } = await adminApi.contracts.getByEmployeeId(employeeId!);
      setContracts(data);
    } catch (error) {
      console.error("获取合同列表失败", error);
    } finally {
      setLoading(false);
    }
  };

  useUpdateEffect(() => {
    if (employeeId) {
      fetchContracts();
    }
  }, [employeeId]);

  // 新增/编辑合同
  const handleSubmit = async (values: any) => {
    try {
      const { contract_period, ...rest } = values;
      const [signing_date, expiration_date] = contract_period;
      const data: CreateContractData = {
        ...rest,
        employee_id: employeeId,

        signing_date: signing_date?.format("YYYY-MM-DD"),
        expiration_date: expiration_date?.format("YYYY-MM-DD"),
      };

      if (editingContract) {
        await adminApi.contracts.update(editingContract.id, data);
        message.success("更新合同成功");
      } else {
        await adminApi.contracts.create(data);
        message.success("添加合同成功");
      }

      setModalVisible(false);
      form.resetFields();
      setEditingContract(null);
      fetchContracts();
    } catch (error) {
      console.error(editingContract ? "更新合同失败" : "添加合同失败", error);
    }
  };

  // 删除合同
  const handleDelete = async (id: number) => {
    try {
      await adminApi.contracts.delete(id);
      message.success("删除合同成功");
      fetchContracts();
    } catch (error) {
      message.error("删除合同失败");
    }
  };

  // 打开编辑模态框
  const handleEdit = async (contract: Contract) => {
    try {
      const { data } = await adminApi.contracts.detail(contract.id);
      setEditingContract(contract);
      form.setFieldsValue({
        ...data,
        contract_period: [
          moment(data.signing_date, "YYYY-MM"),
          moment(data.expiration_date, "YYYY-MM"),
        ],
      });
      setModalVisible(true);
    } catch (error) {
      message.error("获取合同详情失败");
    }
  };

  // 打开新增模态框
  const handleAdd = () => {
    setEditingContract(null);
    form.resetFields();
    form.setFieldsValue({
      status: 1,
    });
    setModalVisible(true);
  };

  const [columns] = useState<ColumnsType<Contract>>([
    {
      title: "合同类型",
      dataIndex: "contract_type",
      key: "contract_type",
      width: 120,
   
    },
    {
      title: "公司",
      dataIndex: "company_subject",
      key: "company_subject",
    },

  
    {
      title: "合同期限",
      key: "contract_period",
      render: (_, record: Contract) => {
        return `${dayjs(record.signing_date).format("YYYY-MM-DD")} ~ ${dayjs(record.expiration_date).format("YYYY-MM-DD")}`;
      },
    },
  
    {
      title: "操作",
      key: "action",
      align: "center",
      width: 200,
      render: (_, record: Contract) => (
        <>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个合同吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ]);

  const { companyList } = useCompanyList();

  return (
    <>
      <Card
        title={`${employeeName}的合同管理`}
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              添加合同
            </Button>
            <Button
              type="primary"
              icon={<RedoOutlined />}
              loading={loading}
              onClick={fetchContracts}
            >
              刷新
            </Button>
          </Space>
        }
      >
        <Table
          bordered
          columns={columns}
          dataSource={contracts}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>

      {/* 新增/编辑合同模态框 */}
      <Modal
        title={editingContract ? "编辑合同" : "添加合同"}
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingContract(null);
        }}
        footer={null}
        width={800}
      >
        <Form form={form} layout="vertical" initialValues={{contract_period:[]}} onFinish={handleSubmit}>
            <Form.Item
              name="contract_type"
              label="合同类型"
              rules={[{ required: true, message: "请输入合同类型" }]}
            >
             <Input placeholder="请输入合同类型" maxLength={10} showCount/>
            </Form.Item>

         
            {/*合同日期  */}
            <Form.Item
              name="contract_period"
              label="合同期限"
              rules={[{ required: true, message: "请选择合同期限" }]}
            >
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                placeholder={["请选择合同开始期限", "请选择合同结束期限"]}
              />
            </Form.Item>

            {/* 公司 */}
            <Form.Item
              name="company_subject"
              label="公司"
              rules={[{ required: true, message: "请选择公司" }]}
            >
              <Select placeholder="请选择公司">
                {companyList.map((item) => (
                  <Select.Option key={item.id} value={item.name}>
                    {item.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

    

          <Form.Item>
            <div style={{ textAlign: "right" }}>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                  setEditingContract(null);
                }}
                style={{ marginRight: 8 }}
              >
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingContract ? "更新" : "添加"}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ContractManagement;
